import { Box, Flex } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { FiArrowRight, FiPlus } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Formik, Form, FieldArray, ErrorMessage } from 'formik';

import {
  getFormValues,
  getTableHeader,
  handleActionStepNext,
  resetRowsIndicatorMappedData,
  setColumnIndicatorMappedData,
  setSelectedAreasProperties,
} from 'redux/DataImportSlice';
import { fetchModelTime } from 'redux/IndicatorPageSlice';
import { fetchBlock, getBlockInputIndicator } from 'redux/PlanPageSlice';

import colors from '../../../dataTheme';
import { controlStyle } from '../../../detailUi';
import ActionStepFooter from '../../ActionStepFooter';
import {
  FieldError,
  FieldLabel,
  StepFooterRow,
  StepHeading,
  StepNotice,
  StepSelect,
} from '../../actionStepUi';

const ColumnType = () => {
  const dispatch: any = useDispatch();
  const { modelId } = useParams();
  const headerNames = useSelector(getTableHeader);
  const getFormValue = useSelector(getFormValues);
  const blockInputIndicator = useSelector(getBlockInputIndicator);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const indicatorsList = Array.isArray(blockInputIndicator) ? blockInputIndicator : [];
  const { columnIndicatorMappedData, multiTimePeriodValue, blockId } = getFormValue ?? {};
  const initialSelectedData = useMemo(() => {
    if (Array.isArray(columnIndicatorMappedData) && columnIndicatorMappedData.length > 0) {
      return columnIndicatorMappedData;
    }

    if (Array.isArray(multiTimePeriodValue) && multiTimePeriodValue.length > 0) {
      return multiTimePeriodValue.map((entry: { key: string }) => ({
        key: entry.key,
        value: null,
      }));
    }

    if (headerNames?.length) return [{ key: headerNames[0], value: null }];
    return [{ key: '', value: null }];
  }, [columnIndicatorMappedData, multiTimePeriodValue, headerNames]);
  useEffect(() => {
    dispatch(fetchModelTime(Number(modelId)));
  }, [dispatch, modelId]);
  useEffect(() => {
    if (blockId) {
      dispatch(fetchBlock(Number(blockId)));
    }
  }, [blockId]);
  const getIndicatorInput = (id: number) => {
    const data = indicatorsList.find((item: any) => item.id === Number(id));

    return data?.name ?? '';
  };

  const handleSubmit = async (form: any, setSubmitting: (data: boolean) => void) => {
    // const submittedData = form.selectedData.reduce((accumulator: any, item: any) => {
    //   accumulator[item.key] = item.value || null;
    //   return accumulator;
    // }, {});
    await dispatch(setColumnIndicatorMappedData(form?.selectedData));
    await dispatch(resetRowsIndicatorMappedData());
    await setSubmitting(false);
    await dispatch(handleActionStepNext());
  };

  return (
    <Box w='100%'>
      <StepHeading
        title='Select indicators'
        subtitle='Map each value column to a block indicator. The selected time-period columns are listed here automatically.'
      />
      {indicatorsList.length === 0 && (
        <StepNotice tone='warning' mb={3}>
          Loading indicators for this block… If this stays empty, the block may have no input
          indicators configured.
        </StepNotice>
      )}
      <Formik
        initialValues={{
          selectedData: initialSelectedData,
        }}
        onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
        enableReinitialize={true}
      >
        {({ values, setFieldValue, isSubmitting }: any) => (
          <Form style={{ width: '100%' }}>
            <Flex direction='column' gap={3}>
              <Flex alignItems='center' gap={3}>
                <FieldLabel flex={1} mb={0}>
                  Column in file
                </FieldLabel>
                <Box w='16px' />
                <FieldLabel flex={1} mb={0}>
                  Indicator in Blox
                </FieldLabel>
              </Flex>
              <FieldArray
                name='selectedData'
                render={() => (
                  <>
                    {values.selectedData.map((selectedItems: any, index: number) => (
                      <Flex key={index} alignItems='center' gap={3} w='100%'>
                        <Box flex={1} minW={0}>
                          <StepSelect
                            value={selectedItems?.key}
                            placeholder='Select column'
                            options={(headerNames ?? [])
                              .filter(
                                (header: any) =>
                                  header === selectedItems?.key ||
                                  !values?.selectedData?.some(
                                    (selected: any) => selected?.key === header,
                                  ),
                              )
                              .map((header: any) => ({ value: header, label: header }))}
                            onChange={(header) => {
                              setFieldValue(`selectedData.${index}.key`, header);
                              dispatch(
                                setSelectedAreasProperties({
                                  [`selectedDatas.${index}.key`]: header,
                                }),
                              );
                            }}
                          />
                        </Box>
                        <FiArrowRight size={16} color={colors.textSecondary} />
                        <Box flex={1} minW={0}>
                          <StepSelect
                            value={
                              getIndicatorInput(selectedItems?.value) ? selectedItems?.value : ''
                            }
                            placeholder='Select indicator'
                            options={indicatorsList
                              .filter(
                                (item: any) =>
                                  item?.id === selectedItems?.value ||
                                  !values.selectedData.some(
                                    (selected: any) => selected.value === item?.id,
                                  ),
                              )
                              .map((item: any) => ({ value: item?.id, label: item?.name }))}
                            onChange={(id) => setFieldValue(`selectedData.${index}.value`, id)}
                          />
                        </Box>
                      </Flex>
                    ))}
                  </>
                )}
              />
              <ErrorMessage name='selectedData' component={FieldError} />
              <Flex
                as='button'
                type='button'
                {...controlStyle}
                alignSelf='flex-start'
                onClick={() => {
                  const currentSelectedData = values.selectedData;
                  const lastSelectedItem = currentSelectedData[currentSelectedData.length - 1];
                  if (lastSelectedItem.value !== null) {
                    setFieldValue('selectedData', [
                      ...currentSelectedData,
                      { key: '', value: null },
                    ]);
                    setShowErrorMessage(false);
                  } else {
                    setShowErrorMessage(true);
                  }
                }}
              >
                <FiPlus size={14} />
                Add column
              </Flex>
              {showErrorMessage && (
                <StepNotice tone='danger'>
                  Value must be filled before adding a new column.
                </StepNotice>
              )}
            </Flex>

            <StepFooterRow>
              <ActionStepFooter
                nextButtonName='Continue'
                prevButtonName='Previous'
                isSubmitting={isSubmitting}
              />
            </StepFooterRow>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ColumnType;
